/**
 * Administration-area access decision.
 *
 * Kept as a pure function, separate from the React guard, for two reasons:
 * the decision is the part worth testing, and `web` has no unit-test runner of
 * its own (tests run under the `functions` vitest binary — see
 * adminAccess.test.ts alongside this file).
 *
 * This is a VISIBILITY control, not a security boundary. Anything shipped to a
 * browser can be read and modified by whoever has it. Every administration
 * operation is independently authorized server-side by `isGlobalAdmin`
 * (functions/src/utils/adminAuth.ts), which is unchanged by this work.
 *
 * Specification: docs/Admin_Area_Specification.md §2.1.
 */

/** What the caller knows at the moment the decision is made. */
export interface AdminAccessInput {
  /** True until BOTH the auth state and the admin claim have resolved. */
  loading: boolean;
  /** Whether anyone is signed in. */
  signedIn: boolean;
  /** Whether the resolved ID token carries `admin: true`. */
  isAdmin: boolean;
}

/**
 * - `wait`     — nothing may render yet. The check has not resolved.
 * - `login`    — no one is signed in; send them to sign in.
 * - `home`     — signed in, not an administrator; send them to the app home.
 * - `allow`    — signed in and an administrator.
 */
export type AdminAccessDecision = "wait" | "login" | "home" | "allow";

/**
 * Order matters. `loading` is checked FIRST and unconditionally: a page that
 * renders and then redirects has already shown what it was meant to hide, so
 * an unresolved check can never fall through to a render. Both `signedIn` and
 * `isAdmin` are meaningless while loading is true.
 */
export function resolveAdminAccess(input: AdminAccessInput): AdminAccessDecision {
  if (input.loading) return "wait";
  if (!input.signedIn) return "login";
  if (!input.isAdmin) return "home";
  return "allow";
}

/**
 * Whether the administration entry point should appear in navigation.
 *
 * Deliberately stricter than `resolveAdminAccess(...) === "allow"`: navigation
 * is rendered on ordinary customer pages, so it must stay hidden while the
 * check is still resolving rather than appearing and then vanishing. There is
 * no "you are not an administrator" affordance — a non-administrator is shown
 * nothing at all, and told nothing about the area existing (§2.1).
 */
export function shouldShowAdminNav(input: AdminAccessInput): boolean {
  return resolveAdminAccess(input) === "allow";
}

export interface SuperAdminAccessInput extends AdminAccessInput {
  /** Whether the resolved ID token carries `superAdmin: true`. */
  isSuperAdmin: boolean;
}

/** A super-admin route fails closed and sends ordinary admins to Users. */
export function resolveSuperAdminAccess(input: SuperAdminAccessInput): AdminAccessDecision {
  const adminDecision = resolveAdminAccess(input);
  if (adminDecision !== "allow") return adminDecision;
  return input.isSuperAdmin ? "allow" : "home";
}
